import { useState, useEffect, useRef } from 'react'

export interface ViewportState {
  height: number
  keyboardHeight: number
  isKeyboardOpen: boolean
}

export const useKeyboardAwareViewport = (): ViewportState => {
  const [viewport, setViewport] = useState<ViewportState>({
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
    keyboardHeight: 0,
    isKeyboardOpen: false,
  })

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const pendingValueRef = useRef<ViewportState | null>(null)
  const previousKeyboardHeightRef = useRef(0)

  useEffect(() => {
    const initialHeight = window.innerHeight

    const updateViewport = () => {
      if (window.visualViewport) {
        const currentHeight = window.visualViewport.height
        const keyboardHeight = window.innerHeight - currentHeight
        const isOpen = keyboardHeight > 150 // Threshold for keyboard detection

        const newState: ViewportState = {
          height: currentHeight,
          keyboardHeight: Math.max(0, keyboardHeight),
          isKeyboardOpen: isOpen,
        }

        // Check if keyboard is opening (going from 0 to positive)
        if (previousKeyboardHeightRef.current === 0 && newState.keyboardHeight > 0) {
          // Instant update when keyboard opens
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
            debounceTimerRef.current = null
          }
          pendingValueRef.current = null
          previousKeyboardHeightRef.current = newState.keyboardHeight
          setViewport(newState)
        } else if (newState.keyboardHeight > 0 && previousKeyboardHeightRef.current > 0) {
          // Debounce when keyboard height changes while already open
          pendingValueRef.current = newState

          if (!debounceTimerRef.current) {
            debounceTimerRef.current = setTimeout(() => {
              if (pendingValueRef.current) {
                previousKeyboardHeightRef.current = pendingValueRef.current.keyboardHeight
                setViewport(pendingValueRef.current)
                pendingValueRef.current = null
              }
              debounceTimerRef.current = null
            }, 400)
          }
        } else {
          // Instant update when keyboard closes or height is 0
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
            debounceTimerRef.current = null
          }
          pendingValueRef.current = null
          previousKeyboardHeightRef.current = newState.keyboardHeight
          setViewport(newState)
        }
      } else {
        // Fallback for browsers without Visual Viewport API
        const currentHeight = window.innerHeight
        const heightDiff = initialHeight - currentHeight
        const isOpen = heightDiff > 150

        const newState: ViewportState = {
          height: currentHeight,
          keyboardHeight: Math.max(0, heightDiff),
          isKeyboardOpen: isOpen,
        }

        // Apply same debounce logic for fallback
        if (previousKeyboardHeightRef.current === 0 && newState.keyboardHeight > 0) {
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
            debounceTimerRef.current = null
          }
          pendingValueRef.current = null
          previousKeyboardHeightRef.current = newState.keyboardHeight
          setViewport(newState)
        } else if (newState.keyboardHeight > 0 && previousKeyboardHeightRef.current > 0) {
          pendingValueRef.current = newState

          if (!debounceTimerRef.current) {
            debounceTimerRef.current = setTimeout(() => {
              if (pendingValueRef.current) {
                previousKeyboardHeightRef.current = pendingValueRef.current.keyboardHeight
                setViewport(pendingValueRef.current)
                pendingValueRef.current = null
              }
              debounceTimerRef.current = null
            }, 400)
          }
        } else {
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
            debounceTimerRef.current = null
          }
          pendingValueRef.current = null
          previousKeyboardHeightRef.current = newState.keyboardHeight
          setViewport(newState)
        }
      }
    }

    // Visual Viewport API listeners
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateViewport)
      window.visualViewport.addEventListener('scroll', updateViewport)
    }

    // Fallback listener
    window.addEventListener('resize', updateViewport)

    // Initial call
    updateViewport()

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateViewport)
        window.visualViewport.removeEventListener('scroll', updateViewport)
      }
      window.removeEventListener('resize', updateViewport)

      // Clean up timer on unmount
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  return viewport
}
